import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiEye, FiUpload } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ImportShipmentModal from '../../components/ImportShipmentModal';

const TruckList = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [etaFilter, setEtaFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [shipperFilter, setShipperFilter] = useState('');
  const [podFilter, setPodFilter] = useState('');
  const [truckerFilter, setTruckerFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadShipments();
  }, [statusFilter, etaFilter, customerFilter, shipperFilter, podFilter, truckerFilter, searchQuery]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '10000');

      const response = await api.get(`/truck?${params}`);
      let data = response.data.data || response.data || [];

      // Client-side filters
      if (etaFilter) data = data.filter(s => s.eta && s.eta.startsWith(etaFilter));
      if (customerFilter) data = data.filter(s => (s.customer || '').toLowerCase().includes(customerFilter.toLowerCase()));
      if (shipperFilter) data = data.filter(s => (s.shipper || '').toLowerCase().includes(shipperFilter.toLowerCase()));
      if (podFilter) data = data.filter(s => (s.port_of_discharge || '').toLowerCase().includes(podFilter.toLowerCase()));
      if (truckerFilter) data = data.filter(s => (s.trucker || '').toLowerCase().includes(truckerFilter.toLowerCase()));

      setShipments(data);
    } catch (error) {
      toast.error('Failed to load shipments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/truck/${id}`);
      toast.success('Shipment deleted successfully');
      setShipments(shipments.filter(s => s.id !== id));
      setConfirmDelete(null);
    } catch (error) {
      toast.error('Failed to delete shipment');
    }
  };

  const columns = [
    {
      key: 'file_no',
      label: 'File No',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/truck/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    {
      key: 'mbl_no',
      label: 'MB/L No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/truck/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    { key: 'container_nos', label: 'Container', sortable: true },
    { key: 'customer', label: 'Customer', sortable: true },
    { key: 'consignee', label: 'Consignee', sortable: true },
    { key: 'port_of_loading', label: 'Port of Loading', sortable: true },
    { key: 'port_of_discharge', label: 'Port of Discharge', sortable: true },
    {
      key: 'etd',
      label: 'ETD',
      sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      key: 'eta',
      label: 'ETA',
      sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    { key: 'trucker', label: 'Trucker', sortable: true },
    {
      key: 'final_destination',
      label: 'Final Destination',
      sortable: true,
      render: (value) => {
        if (!value) return '-';
        const truncated = value.length > 30 ? value.substring(0, 30) + '...' : value;
        return <span title={value}>{truncated}</span>;
      },
    },
  ];

  const renderActions = (row) => [
    <button
      key="view"
      className="btn btn-sm btn-primary"
      onClick={() => navigate(`/truck/${row.id}`)}
      title="View"
    >
      <FiEye /> VIEW
    </button>,
    <button
      key="delete"
      className="btn btn-sm btn-danger"
      onClick={() => setConfirmDelete(row)}
      title="Delete"
    >
      <FiTrash2 />
    </button>,
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">MY SHIPMENT LIST</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn" onClick={() => setShowImport(true)} title="Import from ISF/BOL file">
              <FiUpload /> Import
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/truck/new')}>
              <FiPlus /> New Shipment
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="In Transit">In Transit</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">ETA:</label>
            <input type="date" className="filter-input" style={{ width: '150px' }} value={etaFilter} onChange={(e) => setEtaFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Customer:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Shipper:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={shipperFilter} onChange={(e) => setShipperFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Port of Discharge:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={podFilter} onChange={(e) => setPodFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Trucker:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={truckerFilter} onChange={(e) => setTruckerFilter(e.target.value)} />
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input type="text" className="filter-input" placeholder="Search by file no, MB/L, customer, consignee..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={shipments}
          loading={loading}
          renderActions={renderActions}
          pageSize={20}
        />
      </div>

      {/* Import Modal */}
      <ImportShipmentModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        defaultType="truck"
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete Shipment"
        message={`Are you sure you want to delete shipment ${confirmDelete?.file_no}?`}
        isDangerous
        onConfirm={() => handleDelete(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default TruckList;
